import { useEffect, useRef, useState } from 'react'
import { fetchHistoricalCandles } from '../services/candle-service'
import { calculateIntradayLevels, type IndicatorLevels } from '../lib/technical-indicators'

export function useIndexIndicators(symbol: string, historicalDate?: string | null) {
    const [indexIndicators, setIndexIndicators] = useState<IndicatorLevels | undefined>()
    const [loading, setLoading] = useState(false)
    const [indexCandles, setIndexCandles] = useState<any[]>([])

    // Guard against duplicate in-flight fetches
    const fetchIdRef = useRef(0)

    useEffect(() => {
        const currentFetchId = ++fetchIdRef.current

        const loadIndicators = async () => {
            if (!symbol) return

            setLoading(true)

            try {
                // Determine the correct explicit index instrument key if needed, or pass undefined to let candle-service handle it
                // Using undefined means candle-service will use UPSTOX_INSTRUMENT_KEYS[symbol] natively
                const instrumentKey = undefined 

                // Use allSettled because index data might lack long history in some rare contexts
                const results = await Promise.allSettled([
                    fetchHistoricalCandles(symbol, 60, 'day', instrumentKey, historicalDate),
                    fetchHistoricalCandles(symbol, 180, 'month', instrumentKey, historicalDate),
                    fetchHistoricalCandles(symbol, 180, 'week', instrumentKey, historicalDate),
                    fetchHistoricalCandles(symbol, 1, '1minute', instrumentKey, historicalDate),
                ])

                const dailyData = results[0].status === 'fulfilled' ? results[0].value : []
                const monthlyData = results[1].status === 'fulfilled' ? results[1].value : []
                const weeklyData = results[2].status === 'fulfilled' ? results[2].value : []
                const minuteData = results[3].status === 'fulfilled' ? results[3].value : []

                const referenceDate = historicalDate ? new Date(historicalDate) : new Date()
                const todayStr = referenceDate.toDateString()

                let prevDay = null
                if (dailyData.length > 0) {
                    const lastDay = dailyData[dailyData.length - 1]
                    if (lastDay) {
                        const isToday = new Date(lastDay.timestamp).toDateString() === todayStr
                        const prevDayRaw = isToday
                            ? dailyData.length > 1
                                ? dailyData[dailyData.length - 2]
                                : lastDay
                            : lastDay
                        if (prevDayRaw)
                            prevDay = { high: prevDayRaw.high, low: prevDayRaw.low, close: prevDayRaw.close }
                    }
                }

                let prevMonth = null
                if (monthlyData.length > 0) {
                    const lastMonth = monthlyData[monthlyData.length - 1]
                    if (lastMonth) {
                        const isCurrentMonth =
                            new Date(lastMonth.timestamp).getMonth() === referenceDate.getMonth() &&
                            new Date(lastMonth.timestamp).getFullYear() === referenceDate.getFullYear()
                        const prevMonthRaw = isCurrentMonth
                            ? monthlyData.length > 1
                                ? monthlyData[monthlyData.length - 2]
                                : lastMonth
                            : lastMonth
                        if (prevMonthRaw)
                            prevMonth = {
                                high: prevMonthRaw.high,
                                low: prevMonthRaw.low,
                                close: prevMonthRaw.close,
                            }
                    }
                } else if (weeklyData.length > 0) {
                    const oldestWeek = weeklyData[0]
                    if (oldestWeek)
                        prevMonth = { high: oldestWeek.high, low: oldestWeek.low, close: oldestWeek.close }
                } else if (dailyData.length > 0) {
                    const oldestDay = dailyData[0]
                    if (oldestDay) prevMonth = { high: oldestDay.high, low: oldestDay.low, close: oldestDay.close }
                }

                let prevWeek = null
                if (weeklyData.length > 0) {
                    const lastWeek = weeklyData[weeklyData.length - 1]
                    if (lastWeek) {
                        const daysDiff =
                            (referenceDate.getTime() - new Date(lastWeek.timestamp).getTime()) / (1000 * 3600 * 24)
                        const prevWeekRaw =
                            daysDiff < 7
                                ? weeklyData.length > 1
                                    ? weeklyData[weeklyData.length - 2]
                                    : lastWeek
                                : lastWeek
                        if (prevWeekRaw)
                            prevWeek = { high: prevWeekRaw.high, low: prevWeekRaw.low, close: prevWeekRaw.close }
                    }
                } else if (dailyData.length > 0) {
                    const oldestDay = dailyData[0]
                    if (oldestDay) prevWeek = { high: oldestDay.high, low: oldestDay.low, close: oldestDay.close }
                }

                let prev2Months = null
                if (dailyData.length > 0) {
                    const last60 = dailyData.slice(-60)
                    if (last60.length > 0) {
                        const high = Math.max(...last60.map((d: any) => d.high))
                        const low = Math.min(...last60.map((d: any) => d.low))
                        const close = last60[last60.length - 1]?.close ?? 0
                        prev2Months = { high, low, close }
                    }
                }

                const todayCandles = minuteData.filter(
                    (c: any) => new Date(c.timestamp).toDateString() === todayStr,
                )
                const intradayLevels = calculateIntradayLevels(todayCandles)

                let todayOpen = 0
                let todayHigh = 0
                let todayLow = 0

                if (todayCandles.length > 0 && todayCandles[0]) {
                    todayOpen = todayCandles[0].open || 0
                    todayHigh = Math.max(...todayCandles.map((c: any) => c.high || 0))
                    todayLow = Math.min(...todayCandles.map((c: any) => c.low || 0))
                } else if (dailyData.length > 0) {
                    const dayCandle = dailyData.find((c: any) => new Date(c.timestamp).toDateString() === todayStr)
                    if (dayCandle) {
                        todayOpen = dayCandle.open || 0
                        todayHigh = dayCandle.high || 0
                        todayLow = dayCandle.low || 0
                    }
                }

                const indLevels = {
                    prev2Months,
                    prevDay,
                    prevWeek,
                    prevMonth,
                    first5Min: intradayLevels.first5Min,
                    first15Min: intradayLevels.first15Min,
                    todayOpen,
                    todayHigh,
                    todayLow,
                }

                if (currentFetchId === fetchIdRef.current) {
                    setIndexIndicators(indLevels)
                    setIndexCandles(todayCandles)

                    try {
                        const { HistoryCache } = await import('../lib/idb-cache')
                        await HistoryCache.set(`indicators_index_${symbol}`, [indLevels] as any, '')
                    } catch (e) {
                        /* ignore */
                    }
                }
            } catch (e) {
                console.error('[useIndexIndicators] Failed to load index indicators:', e)
            } finally {
                if (currentFetchId === fetchIdRef.current) {
                    setLoading(false)
                }
            }
        }

        loadIndicators()

        let intervalId: any
        if (!historicalDate) {
            // Poll every 60 seconds for live index candle updates
            intervalId = setInterval(loadIndicators, 60000)
        }

        return () => {
            if (intervalId) clearInterval(intervalId)
        }
    }, [symbol, historicalDate])

    return { indexIndicators, indexCandles, loading }
}
